import { IconButton, Tooltip, Typography } from "@mui/material"
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import EditIcon from '@mui/icons-material/Edit';
import { DocumentAdd, DocumentEdit } from "../../../../models/document";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import SettingsIcon from '@mui/icons-material/Settings';
import { DocumentStructure } from "../../../../models/document-structure";
import MuiInput from "@shared/ui/mui-input/MuiInput";
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { useLocation, useNavigate } from "react-router-dom";


// theme
import { useTheme } from "@emotion/react";
import { ItemsFormType, PaymentsFormType, WorksFormType } from "../../pages/add-document/AddDocument.types";
import { DocumentSettings } from "../../../../models/document-settings";
import { DocumentCarAdd } from "../../../../models/document-car";
import { Car } from "../../../../models/car";
import { DocumentAccountAdd } from "../../../../models/document-account";

import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { FieldsRow } from '@shared/ui/layout/FieldsRow';
import { getValueByPath } from '@shared/lib/utils/object';


interface DocumentCarProps {

    method: "add" | "edit";

    documentForm: UseFormReturn<DocumentAdd> | UseFormReturn<DocumentEdit>;
    worksForm: UseFormReturn<WorksFormType>
    itemsForm: UseFormReturn<ItemsFormType>
    paymentsForm: UseFormReturn<PaymentsFormType>

    settings: DocumentSettings;
    structure: DocumentStructure[];

    isDocumentClosed?: boolean;
}




function DocumentCar({ method, isDocumentClosed, documentForm, worksForm, itemsForm, paymentsForm, settings, structure }: DocumentCarProps) {

    const navigate = useNavigate();
    const { state } = useLocation(); // state data from another page
    const theme = useTheme();

    function renderCarField<T extends FieldValues>(field: DocumentStructure, documentForm: UseFormReturn<T>) {

        const isCarField = field.type === "רכב" && field.show_form;

        if(isCarField){
            return <MuiInput 
                key={field.field_name}
                field={field}
                variant='outlined'
                useForm={documentForm}
                prefix="car"
                showRequired
                disabled={isDocumentClosed}
                searchType="רכב"
                onSelect={onCarSelect}
            />
        }        
    }

    function onCarSelect(car: Car){

        const documentCar: DocumentCarAdd = {...car, id: undefined, car_id: car.id};
        const documentAccount: DocumentAccountAdd | null = car.customer? {...car.customer, id: undefined, account_id: car.customer?.id} : null;

        const form = documentForm as UseFormReturn<DocumentAdd>;

        structure
            ?.filter(field => field.type === "רכב" && field.show_form)
            .forEach(field => {
                const value = getValueByPath<any>(documentCar, field.field_name) ?? null;
                form.setValue(`car.${field.field_name}` as Path<DocumentAdd>, value, { shouldDirty: true });
            });

        form.setValue("car", documentCar, { shouldDirty: true });

        structure
            ?.filter(field => field.type === "חשבון" && field.show_form)
            .forEach(field => {
                const value = documentAccount ? getValueByPath<any>(documentAccount, field.field_name) ?? null : null;
                form.setValue(`document_account.${field.field_name}` as Path<DocumentAdd>, value, { shouldDirty: true });
            });

        form.setValue("document_account", documentAccount, { shouldDirty: true });
    }
    

    function createState(){
        return {
            ...state,
            document_settings_id: settings?.id,

            document_account: documentForm.getValues().document_account,
            car: documentForm.getValues().car,

            works: worksForm.getValues().works,
            items: itemsForm.getValues().items,
            payments: paymentsForm.getValues().payments,
            reference: documentForm.getValues().reference,
        }
    }


    function createBreadcrumbData(state: any){

        const thisPageLink = 'id' in documentForm.getValues() ?
            `/documents/${(documentForm as  UseFormReturn<DocumentEdit>).getValues("id")}` :
            `/documents/add?documentType=${settings?.id}`;

        return [
            {
                name: "מסמכים",
                link: "/documents",
            },
            {
                name: "הוספת מסמך",
            },
            {
                name: `${settings?.name}`,
                link: thisPageLink,
                state: state
            }
        ];
    }

    
    function editCarStructure() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState)

        navigate(`/documents/structure/car?documentType=${settings.id}`, {
            replace: true,
            state: {
                breadcrumbData: [...breadcrumbData, { name: "הגדרת מבנה" }, { name: "רכב" }]
            }
        });
    }


    
    function browseCar() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState)

        navigate(`/cars`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "בחירת רכב" }]
            }
        });
    }


    function addCar() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState)

        navigate(`/cars/add`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "הוספת כרטיס רכב" }]
            }
        });
    }


    function editCar() {

        const currentState = createState();
        const breadcrumbData = createBreadcrumbData(currentState)

        navigate(`/cars/${documentForm.getValues().car?.car_id ?? documentForm.getValues().car?.id}`, {
            replace: true,
            state: {
                ...currentState,
                breadcrumbData: [...breadcrumbData, { name: "עריכת כרטיס רכב" }]
            }
        });
    }

    /**
     * clean the car fields
     */
    function cleanFields(){
        const form = documentForm as UseFormReturn<DocumentAdd>;

        structure
            ?.filter(field => field.type === "רכב" && field.show_form)
            .forEach(field => {
                form.setValue(`car.${field.field_name}` as Path<DocumentAdd>, null, { shouldDirty: true });
            });

        form.setValue("car", null);
    }


    if (method === "add") {

        return (
            <>
                <div className="title">
                    <div>
                        <Typography variant="body1" component="span">
                            פרטי רכב:
                        </Typography>
                        <Tooltip title="בחירת כרטיס רכב מתוך דפדוף" placement="top">
                            <IconButton onClick={browseCar} sx={{ color: theme.colors.main_color }}>
                                <AutoStoriesIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="הוספת כרטיס רכב" placement="top">
                            <span>
                                <IconButton onClick={addCar} disabled={!!documentForm.getValues().car?.car_id} sx={{ color: theme.colors.main_color }}>
                                    <BookmarkAddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="עריכת כרטיס רכב" placement="top">
                            <span>
                                <IconButton onClick={editCar} disabled={!documentForm.getValues().car?.car_id} sx={{ color: theme.colors.main_color }}>
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="איפוס שדות" placement="top">
                            <span>
                                <IconButton onClick={cleanFields} sx={{ color: theme.colors.main_color }}>
                                    <DeleteSweepIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                    <div>
                        <Tooltip title="הגדרת שדות רכב" placement="top">
                            <IconButton onClick={editCarStructure} sx={{ color: theme.colors.main_color }}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                <FieldsRow>
                    {structure?.sort((a, b) => (a.location || 0) - (b.location || 0)).map(field => {
                        return renderCarField(field, documentForm as UseFormReturn<DocumentEdit>)
                    })}
                </FieldsRow>
                    
            </>
        )
    }

    else if (method === "edit") {

        return (
            <>
                <div className="title">
                    <div>
                        <Typography variant="body1" component="span" sx={{
                            color: !isDocumentClosed ? "text.primary" : "action.disabled",
                        }}>
                            פרטי רכב:
                        </Typography>
                        <Tooltip title="בחירת כרטיס רכב מתוך דפדוף" placement="top">
                            <span>
                                <IconButton onClick={browseCar} sx={{ color: theme.colors.main_color }} disabled={isDocumentClosed}>
                                    <AutoStoriesIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="הוספת כרטיס רכב" placement="top">
                            <span>
                                <IconButton onClick={addCar} disabled={!!documentForm.getValues().car?.id || isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <BookmarkAddIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="עריכת כרטיס רכב" placement="top">
                            <span>
                                <IconButton onClick={editCar} disabled={!documentForm.getValues().car?.id || isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="איפוס שדות" placement="top">
                            <span>
                                <IconButton onClick={cleanFields} disabled={isDocumentClosed} sx={{ color: theme.colors.main_color }}>
                                    <DeleteSweepIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                    <div>
                        <Tooltip title="הגדרת שדות רכב" placement="top">
                            <IconButton onClick={editCarStructure} sx={{ color: isDocumentClosed? "rgba(0,0,0,0.3)" : theme.colors.main_color }}>
                                <SettingsIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                <FieldsRow>
                    {structure?.sort((a, b) => (a.location || 0) - (b.location || 0)).map(field => {
                        return renderCarField(field, documentForm as UseFormReturn<DocumentEdit>)
                    })}
                </FieldsRow>
            </>
        )
    }


}

export default DocumentCar